import React, { useEffect, useState } from 'react';
import { Banknote } from 'lucide-react';
import api from '@/api';
import PillHero from './PillHero';

// FB-COV-FX2 — region filter drives the home currency. The user's TOP region
// chip in PulseDashboard maps to a single currency, and we display ONLY
// that currency vs the other 5 in the 6-currency basket.
const REGION_TO_CCY = {
  US: 'USD', UK: 'GBP', GB: 'GBP', IN: 'INR',
  JP: 'JPY', CN: 'CNY', EU: 'EUR',
};

function homeCcyFromRegion(region) {
  if (!region) return 'USD';
  return REGION_TO_CCY[region.toUpperCase()] || 'USD';
}

export default function ForexHero({ region }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const home = homeCcyFromRegion(region);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    (async () => {
      try {
        const res = await api.getForexMajorsToday(home);
        if (!cancel) setData(res);
      } catch (_e) {
        if (!cancel) setData({ available: false });
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [home]);

  if (loading || !data?.available) return null;

  const asOf = data.as_of
    ? new Date(data.as_of).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })
    : '';

  // Backend returns only the 5 home-currency-leading pairs (home vs each of
  // the other 5 currencies). No matrix.
  const tiles = (data.tiles || []).map((t) => {
    const v = t.value ?? t.value_usd;
    const decimals = v == null ? 2 : v < 10 ? 4 : 2;
    return {
      label: t.label,
      primary: v == null ? '—' : Number(v).toFixed(decimals),
      badgeDirection: t.direction,
      badgeLabel: `${(t.change_24h_pct ?? 0) >= 0 ? '+' : ''}${(t.change_24h_pct ?? 0).toFixed(2)}% 24h`,
    };
  });

  return (
    <PillHero
      testId="forex-hero"
      Icon={Banknote}
      accent="#3B82F6"
      title={`${home} · Live FX`}
      subtitle={`As of ${asOf} · ${home} vs USD, EUR, GBP, JPY, INR, CNY`}
      tiles={tiles}
      sourceNote={data.source_note}
    />
  );
}
